import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  HStack,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  useTheme,
  VStack,
  Heading,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import React, {useState, useEffect, useRef} from 'react';
import Icon from '../../components/Icon';
import Modal from '../../components/Modals/Modal';
import {TextA18, TextG10, TextG12, TextG16} from '../../components/typography';
import {useInfiniteGlobalSearchWithParams} from '../../hooks/useFetchGlobalSearch';
import useGetUserLocation from '../../hooks/useUserLocation';
import {calculateDistance, getDistanceString} from '../../utils/distance';
import {Event} from '../../types/article';
import {fetchPlace} from '../../endpoints/api';
import {ToastMessage} from '../../components/toast';

interface EventSelectModalProps {
  selectedEvent?: Event;
  onSelectEvent?: (event: Event) => void;
  onRemoveEvent?: (event: Event) => void;
  modalTitle?: string;
  buttonPlaceholder?: string;
  showButton?: boolean;
  showModal?: boolean;
  setShowModal?: (show: boolean) => void;
  closeOnSelect?: boolean;
  isLoading?: boolean;
  disableSelection?: boolean;
  placeId?: number; // Add place filter parameter
}

/**
 * A generic event selector modal that allows selecting a single event.
 * The parent component should handle what happens when an event is selected or removed.
 */
const EventSelectModal = ({
  selectedEvent,
  onSelectEvent,
  onRemoveEvent,
  modalTitle = 'Select an Event',
  buttonPlaceholder = 'Choose an event',
  showButton = true,
  showModal = false,
  setShowModal,
  closeOnSelect = true,
  isLoading: externalLoading = false,
  disableSelection = false,
  placeId, // Add place filter parameter
}: EventSelectModalProps) => {  const [show, setShow] = useState(false);
  const [term, setTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [placeEvents, setPlaceEvents] = useState<Event[]>([]);
  const [isLoadingPlaceEvents, setIsLoadingPlaceEvents] = useState(false);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userLocation = useGetUserLocation();
  const theme = useTheme();

  // Handle debouncing for search term
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedTerm(term);
    }, 500);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [term]);
  useEffect(() => {
    setShow(showModal);
  }, [showModal]);  // Clear search term when placeId changes to show all place events initially
  useEffect(() => {
    if (placeId) {
      setTerm('');
      setDebouncedTerm('');
    }
  }, [placeId]);
  // Load place events when placeId is provided
  useEffect(() => {
    const loadPlaceEvents = async () => {
      if (placeId) {
        try {
          setIsLoadingPlaceEvents(true);
          const response = await fetchPlace({ id: placeId });
          const events = response.data?.events || [];
          setPlaceEvents(events);
        } catch (error) {
          console.error('Failed to load place events:', error);
          ToastMessage({status: 'error', text: 'Failed to load events for this place.'});
          setPlaceEvents([]);
        } finally {
          setIsLoadingPlaceEvents(false);
        }
      } else {
        setPlaceEvents([]);
      }
    };

    loadPlaceEvents();
  }, [placeId]);  const {data, isLoading} = useInfiniteGlobalSearchWithParams({
    term: debouncedTerm.length >= 3 && !placeId ? debouncedTerm : '', // Only search globally when no place is selected
    type: 'event',
    sort: {
      by: 'start_date_time',
      direction: 'asc',
    },
    // Remove place_id from global search since we handle place filtering separately
  });

  // Function to filter place events locally based on search term
  const getFilteredPlaceEvents = () => {
    if (!debouncedTerm.trim()) {
      return placeEvents;
    }
    
    const searchTermLower = debouncedTerm.toLowerCase();
    return placeEvents.filter(event => 
      event.title?.toLowerCase().includes(searchTermLower) ||
      event.name?.toLowerCase().includes(searchTermLower) ||
      event.description?.toLowerCase().includes(searchTermLower)
    );
  };

  // Use different data sources based on whether a place is selected
  const searchRes = placeId 
    ? getFilteredPlaceEvents() // For place events, filter locally
    : (data?.pages[0]?.data?.data || []); // For global search, use API results

  // Format event date in a more readable way
  const formatEventDate = (dateString) => {
    if (!dateString) return '';
    const date = dayjs.utc(dateString).local();
    return date.format('MMM D, YYYY · h:mm A');
  };

  // Generic handler for event selection
  const handleSelectEvent = (event: Event) => {
    // Prevent selection during loading or when disabled
    if (disableSelection || externalLoading) return;

    try {
      // Call the external handler passed by the parent component
      if (onSelectEvent) {
        onSelectEvent(event);
      }

      // Close the modal if configured to do so
      if (closeOnSelect) {
        setShow(false);
        if (setShowModal) {
          setShowModal(false);
        }
      }
    } finally {
    }
  };

  // Generic handler for event removal
  const handleRemoveEvent = (event: Event) => {
    if (disableSelection || externalLoading) return;

    try {
      // Call the external handler passed by the parent component
      if (onRemoveEvent) {
        onRemoveEvent(event);
      }
    } finally {
    }
  };  // Determine if we're in a loading state
  const isLoadingState = externalLoading || (placeId ? isLoadingPlaceEvents : isLoading);

  return (
    <>
      {showButton && (
        <Button
          onClick={() => setShow(true)}
          height="auto"
          alignItems="flex-end"
          w="100%"
          px="16px"
          py="12px"
          justifyContent="left"
          background="transparent"
          border="1px solid #E5E5E5"
          borderRadius="30px">
          <Flex gap="5px" align="center">
            {selectedEvent && selectedEvent.image_url && (
              <Image
                objectFit="cover"
                minW="30px"
                minH="30px"
                maxH="30px"
                maxW="30px"
                borderRadius="full"
                src={selectedEvent.image_url}
              />
            )}
            <TextG12
              fontWeight="500"
              color={selectedEvent ? theme.colors.lightBlack : theme.colors.lightestGrey}>
              {selectedEvent?.title || selectedEvent?.name || buttonPlaceholder}
            </TextG12>
          </Flex>
        </Button>
      )}
      <Modal
        closeButton
        parentCallback={() => {
          setShow(false);
          if (setShowModal) {
            setShowModal(false);
          }
        }}
        header={
          <TextA18 fontWeight="700" mt={2}>
            {modalTitle}
          </TextA18>
        }
        children={
          <VStack w="100%" spacing={4}>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon iconName="fi-rr-search" color="gray.300" />
              </InputLeftElement>

              <Input
                borderRadius="40px"
                mb={2}
                onChange={(value) => setTerm(value.target.value)}
                type="search"
                placeholder="Search for events"
                isDisabled={isLoadingState}></Input>
            </InputGroup>

            <Box w="100%" mb={4}>
              <Heading size="sm" mb={2}>
                Currently Selected Event
              </Heading>
              <VStack w="100%" align="stretch" spacing={2}>
                {selectedEvent ? (
                  <HStack
                    p={2}
                    borderRadius="md"
                    border="1px solid"
                    borderColor="gray.200"
                    justifyContent="space-between">
                    <HStack>
                      {selectedEvent?.image_url && (
                        <Image
                          objectFit="cover"
                          w="40px"
                          h="40px"
                          borderRadius="full"
                          src={selectedEvent?.image_url}
                        />
                      )}
                      <VStack align="start" spacing={0}>
                        <TextG12 fontWeight="500">
                          {selectedEvent?.title || selectedEvent?.name}
                        </TextG12>
                        <TextG10 color={theme.colors.grey}>
                          {formatEventDate(selectedEvent?.start_date_time)}
                        </TextG10>
                      </VStack>
                    </HStack>
                    {onRemoveEvent && (
                      <Button
                        size="sm"
                        colorScheme="red"
                        variant="ghost"
                        isDisabled={isLoadingState}
                        onClick={() => handleRemoveEvent(selectedEvent)}
                        opacity={isLoadingState ? 0.5 : 1}>
                        <Icon iconName="fi-rr-trash" />
                      </Button>
                    )}
                  </HStack>
                ) : (
                  <Box
                    p={4}
                    borderRadius="md"
                    border="1px dashed"
                    borderColor="gray.200"
                    textAlign="center">
                    <TextG12 color={theme.colors.grey}>No event selected</TextG12>
                  </Box>
                )}
              </VStack>
              <Divider my={4} />
            </Box>

            {/* Show loading indicator at the top of the list instead of replacing the whole list */}
            {isLoadingState && (
              <Center py={2}>
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor={theme.colors.lightGrey}
                  color={theme.colors.lightBlue}
                  size="sm"
                />
                <TextG12 ml={2} color={theme.colors.grey}>Changing Events...</TextG12>
              </Center>
            )}

            {/* Always show search results, but disable them when loading */}
            {searchRes.length > 0 ? (
              <VStack w="100%" maxHeight="350px" overflowY="scroll" mt={"0px !important"}>
                {searchRes.map((event, i) => {
                  // Check if this search result is the currently selected event
                  const isCurrentlySelected = selectedEvent?.id === event.id;

                  return (
                    <Box key={i} w="100%">
                      <HStack
                        boxShadow={isCurrentlySelected ? 'md' : 'none'}
                        transition="all 0.5s"
                        py={2}
                        borderRadius="12px"
                        border={
                          isCurrentlySelected
                            ? `1px solid ${theme.colors.blue}`
                            : '1px solid transparent'
                        }
                        opacity={isLoadingState ? 0.6 : isCurrentlySelected ? 0.7 : 1}
                        w="100%"
                        onClick={() =>
                          !isCurrentlySelected && !isLoadingState && handleSelectEvent(event)
                        }
                        cursor={isCurrentlySelected || isLoadingState ? 'default' : 'pointer'}
                        pointerEvents={isLoadingState ? 'none' : 'auto'}
                        position="relative">
                        {event.image_url && (
                          <Image
                            objectFit="cover"
                            minW="75px"
                            minH="75px"
                            maxH="75px"
                            maxW="75px"
                            borderRadius="full"
                            src={event.image_url}
                            filter={isLoadingState ? "grayscale(30%)" : "none"}
                          />
                        )}
                        <VStack w="100%" alignItems={'flex-start'}>
                          <TextG16
                            transition="all 0.5s"
                            isTruncated="&hellip;"
                            noOfLines={2}
                            fontWeight="500"
                            color={
                              isCurrentlySelected ? theme.colors.blue : theme.colors.lightBlack
                            }>
                            {event.title || event.name}
                            {isCurrentlySelected && ' (Currently selected)'}
                          </TextG16>
                          {userLocation.data?.latitude &&
                            userLocation.data.longitude &&
                            event?.latitude &&
                            event?.longitude && (
                              <TextG10 color={theme.colors.lightBlack}>
                                {`${getDistanceString(
                                  calculateDistance(
                                    {
                                      latitude: Number(userLocation.data?.latitude),
                                      longitude: Number(userLocation.data?.longitude),
                                    },
                                    {
                                      latitude: Number(event.latitude),
                                      longitude: Number(event.longitude),
                                    }
                                  )
                                )} away`}
                              </TextG10>
                            )}
                          <TextG10 color={theme.colors.grey}>
                            {formatEventDate(event.start_date_time)}
                          </TextG10>
                          {event.location && (
                            <TextG10 color={theme.colors.grey}>{event.location}</TextG10>
                          )}
                        </VStack>
                      </HStack>
                      <Divider />
                    </Box>
                  );
                })}
              </VStack>
            ) : (
              <Box py={8} textAlign="center">
                <TextG12 color={theme.colors.grey}>
                  {debouncedTerm.length >= 3
                    ? 'No events found matching your search'
                    : 'Type at least 3 characters to search for events'}
                </TextG12>
              </Box>
            )}
          </VStack>
        }
        show={show}
      />
    </>
  );
};

export default EventSelectModal;
